<template>
  <div v-if="supportStep" class="onboarding-support">
    <a
      :href="supportUrl"
      class="onboarding-support__link"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Hablar con soporte de ItSave por WhatsApp"
      @click="trackSupportClick"
    >
      <span class="onboarding-support__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path
            d="M19.1 4.9A9.8 9.8 0 0 0 3.7 16.7L2.3 22l5.4-1.4A9.8 9.8 0 0 0 19.1 4.9Zm-7 15.1a8 8 0 0 1-4.1-1.1l-.3-.2-3.2.8.9-3.1-.2-.3A8 8 0 1 1 12.1 20Zm4.4-6c-.2-.1-1.4-.7-1.7-.8-.2-.1-.4-.1-.6.2l-.7.9c-.1.2-.3.2-.5.1-1.4-.7-2.4-1.3-3.3-2.9-.2-.3.2-.4.6-1 .1-.2.1-.4 0-.5l-.7-1.8c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5 1.9.8 2.7.9 3.7.8 1.1-.2 2.9-1.2 3.3-2.3.4-1.1.4-2 .3-2.2-.2-.2-.4-.3-.7-.4Z"
          />
        </svg>
      </span>
      <span class="onboarding-support__copy">
        <small>¿Problemas?</small>
        <strong>Hablar con soporte</strong>
      </span>
    </a>
    <span class="onboarding-support__mobile-space" aria-hidden="true"></span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const supportSteps = Object.freeze({
  "/onboarding": "Crear mi cuenta",
  "/uno": "Crear mi cuenta",
  "/anuala": "Crear mi cuenta",
  "/anualb": "Crear mi cuenta",
  "/unob": "Crear mi cuenta",
  "/unoc": "Completar mi registro",
  "/unod": "Crear mi cuenta",
  "/unoe": "Crear mi cuenta",
  "/unof": "Crear mi cuenta",
  "/unog": "Crear mi cuenta",
  "/procesando_pago": "Activar mi plan",
  "/pago_anual": "Activar mi plan",
  "/cGFnb19leGl0b3Nv": "Confirmar mi pago",
  "/cGFnb19mYWxsaWRv": "Confirmar mi pago",
  "/dos": "Datos de mi empresa",
  "/tres": "Enlazar WhatsApp",
  "/cuatro": "Conectar el SII",
  "/cinco": "Conectar mi banco",
  "/onboarding_graph": "Completar la configuración",
  "/crear_cuenta_obfact": "Crear mi cuenta",
  "/datos_empresa_obfact": "Datos de mi empresa",
  "/enlazar_whatsapp_obfact": "Enlazar WhatsApp",
  "/credenciales_facturador_obfact": "Conectar el SII",
  "/pagar_suscripcion_facturador": "Activar mi plan",
  "/lN1p6jVGwobP8iTzKo5EdQWt": "Confirmar mi pago",
  "/z5vU7S7MJyQZZqAmDTvFfFse": "Confirmar mi pago",
  "/U4EUYSQEjMIXerQ-qOioCn6F": "Confirmar mi pago",
});

const supportStep = computed(() => supportSteps[route.path] || "");
const supportMessage = computed(
  () => `Hola, tengo problemas en el paso "${supportStep.value}" de ItSave. Necesito ayuda.`,
);
const supportUrl = computed(
  () => `https://wa.me/56983708288?text=${encodeURIComponent(supportMessage.value)}`,
);

function trackSupportClick() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "onboarding_support_whatsapp_click",
    onboarding_step: supportStep.value,
    source_path: route.fullPath,
  });
}
</script>

<style scoped>
.onboarding-support {
  --support-green: #22a968;
  --support-ink: #1f2a44;
  position: relative;
  z-index: 70;
  font-family: "Poppins", sans-serif;
}

.onboarding-support__link {
  position: fixed;
  right: 24px;
  bottom: max(22px, env(safe-area-inset-bottom));
  z-index: 70;
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 58px;
  padding: 9px 18px 9px 10px;
  border: 1px solid #e0e4ed;
  border-radius: 16px;
  color: var(--support-ink);
  background: #fff;
  box-shadow: 0 14px 38px rgba(24, 36, 66, 0.2);
  text-decoration: none;
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
}

.onboarding-support__link:hover,
.onboarding-support__link:focus-visible {
  border-color: #b9e3ce;
  box-shadow: 0 17px 42px rgba(24, 36, 66, 0.25);
  transform: translateY(-2px);
}

.onboarding-support__link:focus-visible {
  outline: 3px solid rgba(122, 90, 248, 0.3);
  outline-offset: 3px;
}

.onboarding-support__icon {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 12px;
  color: #fff;
  background: var(--support-green);
}

.onboarding-support__icon svg {
  width: 25px;
  height: 25px;
  fill: currentColor;
}

.onboarding-support__copy {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.onboarding-support__copy small {
  color: #68748e;
  font-size: 10.5px;
  font-weight: 500;
}

.onboarding-support__copy strong {
  margin-top: 2px;
  font-size: 13px;
  font-weight: 700;
}

.onboarding-support__mobile-space { display: none; }

@media (max-width: 900px) {
  .onboarding-support__link {
    right: 12px;
    bottom: max(12px, env(safe-area-inset-bottom));
    left: 12px;
    justify-content: center;
    min-height: 56px;
    padding: 8px 16px 8px 9px;
    border-color: #d8e8e0;
    border-radius: 15px;
  }

  .onboarding-support__icon {
    width: 39px;
    height: 39px;
    flex-basis: 39px;
  }

  .onboarding-support__copy {
    flex-direction: row;
    align-items: baseline;
    gap: 5px;
  }

  .onboarding-support__copy small,
  .onboarding-support__copy strong {
    margin: 0;
    font-size: 13px;
  }

  .onboarding-support__mobile-space {
    display: block;
    height: calc(78px + env(safe-area-inset-bottom));
    pointer-events: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .onboarding-support__link { transition: none; }
}
</style>
